/*****************************************************************
 *  ZOHO BRIDGE PANEL — THE "CUSTOMERS" TAB (links)                *
 *  File: apps/web/src/pages/bridge/CustomersTab.js                *
 *  What it does: which Zoho contact is linked to which Butler     *
 *  customer, and a "link to a different customer" box that        *
 *  searches Customer Butler (through Customer Butler's own door). *
 *  Old links are never deleted; they show as "Replaced".          *
 *  Reads: GET /links, Customer Butler search.                     *
 *  Writes: POST /links/:id/relink, only on confirm.               *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 — THE RELINK BOX                                     *
 *  SECTION 2 — THE TAB                                            *
 *****************************************************************/

import React, { useCallback, useEffect, useState } from 'react';
import { BridgeIcon } from './bridgeIcons';
import { BRIDGE_WORDS, MATCH_NAMES } from './bridgeWords';
import { LoadState, ToneChip, WhyBox, Pager, formatWhen } from './bridgeParts';
import { fetchLinks, relinkCustomer, searchButlerCustomers } from './bridgeApi';

/*****************************************************************
 *  SECTION 1 — THE RELINK BOX                                     *
 *****************************************************************/

/* ---- RelinkBox({ linkRow, onDone, onCancel }) ------------------
   Search, pick a customer, say why, confirm.                    */
function RelinkBox({ linkRow, onDone, onCancel }) {
  const [searchText, setSearchText] = useState('');
  const [foundCustomers, setFoundCustomers] = useState([]);
  const [chosenCustomer, setChosenCustomer] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [answerText, setAnswerText] = useState('');
  const searchBoxId = 'zb-relink-search-' + linkRow.id;

  /* ---- runSearch() -- ask Customer Butler ---- */
  async function runSearch(event) {
    event.preventDefault();

    const answer = await searchButlerCustomers(searchText.trim());

    setFoundCustomers(answer.data || []);
    setAnswerText(answer.ok ? '' : answer.message || BRIDGE_WORDS.loadFailed);
  }

  /* ---- confirmRelink(whyText) -- send it ---- */
  async function confirmRelink(whyText) {
    setIsBusy(true);

    const answer = await relinkCustomer(linkRow.id, chosenCustomer.id, whyText);

    setIsBusy(false);

    if (!answer.ok) {
      setAnswerText(answer.message || BRIDGE_WORDS.loadFailed);
      return;
    }

    onDone();
  }

  return (
    <div className="zb-drawer">
      <form className="zb-row" onSubmit={runSearch}>
        <label htmlFor={searchBoxId} className="zb-visually-hidden">{BRIDGE_WORDS.searchCustomers}</label>
        <input
          id={searchBoxId}
          className="zb-input"
          type="search"
          value={searchText}
          placeholder={BRIDGE_WORDS.searchCustomers}
          onChange={(event) => setSearchText(event.target.value)}
        />
        <button type="submit" className="zb-button zb-button-plain" disabled={searchText.trim().length < 2}>
          {BRIDGE_WORDS.search}
        </button>
        <button type="button" className="zb-button zb-button-plain" onClick={onCancel}>
          {BRIDGE_WORDS.cancel}
        </button>
      </form>

      {answerText ? <div className="zb-notice zb-notice-bad" role="alert"><span>{answerText}</span></div> : null}

      {!chosenCustomer ? (
        <ul className="zb-list">
          {foundCustomers.map((oneCustomer) => (
            <li className="zb-list-row" key={oneCustomer.id}>
              <div className="zb-list-main">
                <strong>{oneCustomer.displayName}</strong>
                <span className="zb-small">{oneCustomer.primaryEmail || ''} {oneCustomer.primaryPhone || ''}</span>
              </div>
              <button type="button" className="zb-button zb-button-plain" onClick={() => setChosenCustomer(oneCustomer)}>
                {BRIDGE_WORDS.chooseThis}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <WhyBox
          label={BRIDGE_WORDS.whyLabel + ' — ' + BRIDGE_WORDS.linkedTo + ' ' + chosenCustomer.displayName}
          confirmWord={BRIDGE_WORDS.relink}
          tone="primary"
          busy={isBusy}
          onConfirm={confirmRelink}
          onCancel={() => setChosenCustomer(null)}
        />
      )}
    </div>
  );
}

/*****************************************************************
 *  SECTION 2 — THE TAB                                            *
 *****************************************************************/

export default function CustomersTab() {
  const [linkRows, setLinkRows] = useState([]);          // the links
  const [loadState, setLoadState] = useState('loading'); // loading / ready / error
  const [showOld, setShowOld] = useState(false);         // include replaced links
  const [relinkingId, setRelinkingId] = useState('');    // the link with the box open
  const [pageNumber, setPageNumber] = useState(1);       // which page (defect 372)
  const [pageInfo, setPageInfo] = useState({ total: 0, pageSize: 50 }); // the total and page size

  /* ---- loadLinks() -- one page ---- */
  const loadLinks = useCallback(async () => {
    setLoadState('loading');

    const answer = await fetchLinks(showOld, pageNumber);

    if (!answer.ok || !answer.data) {
      setLoadState('error');
      return;
    }

    setLinkRows(answer.data.rows || []);
    setPageInfo({ total: answer.data.total, pageSize: answer.data.pageSize });
    setLoadState('ready');
  }, [showOld, pageNumber]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  return (
    <section aria-labelledby="zb-tab-customers-title">
      <h2 id="zb-tab-customers-title" className="zb-visually-hidden">{BRIDGE_WORDS.tabCustomers}</h2>

      <label className="zb-check">
        <input type="checkbox" checked={showOld} onChange={(event) => setShowOld(event.target.checked)} />
        <span>{BRIDGE_WORDS.showOldLinks}</span>
      </label>

      {loadState !== 'ready' ? <LoadState state={loadState} onRetry={loadLinks} /> : null}
      {loadState === 'ready' && linkRows.length === 0 ? <LoadState state="empty" emptyWord={BRIDGE_WORDS.linksEmpty} /> : null}

      {loadState === 'ready' && pageInfo.total > 0 ? (
        <Pager page={pageNumber} pageSize={pageInfo.pageSize} total={pageInfo.total} onPage={setPageNumber} />
      ) : null}

      <ul className="zb-list">
        {linkRows.map((oneLink) => (
          <li className="zb-list-row zb-list-row-stack" key={oneLink.id}>
            <div className="zb-list-main">
              <BridgeIcon name="link" />
              {/* WHY (2026-10-13, help-run defect 374): the Zoho contact's own
                  NAME comes first; the Zoho number stays beside it, smaller. */}
              <strong>{oneLink.zohoName || BRIDGE_WORDS.zohoContact}</strong>
              <span className="zb-small">{BRIDGE_WORDS.zohoContact} {oneLink.zohoContactId}</span>
              <span>
                {BRIDGE_WORDS.linkedTo}{' '}
                <strong>{oneLink.customer ? oneLink.customer.displayName : oneLink.customerId}</strong>
              </span>
            </div>
            <div className="zb-row zb-row-tight">
              {oneLink.supersededAt
                ? <ToneChip tone="neutral" icon="history" word={BRIDGE_WORDS.replaced} />
                : <ToneChip tone="ok" icon="check" word={'By ' + (MATCH_NAMES[oneLink.how] || oneLink.how)} />}
              <span className="zb-small">{formatWhen(oneLink.linkedAt)}{oneLink.why ? ' — "' + oneLink.why + '"' : ''}</span>
            </div>
            {!oneLink.supersededAt && relinkingId !== oneLink.id ? (
              <button type="button" className="zb-button zb-button-plain" onClick={() => setRelinkingId(oneLink.id)}>
                {BRIDGE_WORDS.relink}
              </button>
            ) : null}
            {relinkingId === oneLink.id ? (
              <RelinkBox
                linkRow={oneLink}
                onCancel={() => setRelinkingId('')}
                onDone={() => {
                  setRelinkingId('');
                  loadLinks();
                }}
              />
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
